import { ProfileResponseModel } from '../types/Profile';
import { UserDocument } from '../types/UserDocument';
import { EditProfilePayload } from '../pages/EditProfile';
import * as yup from 'yup';
import { api } from './api';
import axios from 'axios';
import { normalizeProfileFollowData } from './followingProfile';

const logApiError = (context: string, error: unknown) => {
  if (axios.isAxiosError(error)) {
    console.error(`${context} failed`, {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      statusText: error.response?.statusText,
      method: error.config?.method,
      url: error.config?.url,
      responseData: error.response?.data,
    });
    return;
  }

  console.error(`${context} failed`, error);
};

const normalizeWebsiteUrl = <T extends { websiteUrl?: string; WebsiteUrl?: string }>(
  response: T
): T => {
  const websiteUrl = response.websiteUrl ?? response.WebsiteUrl;

  return {
    ...response,
    websiteUrl,
  };
};

const stripProtocol = (value: string) => value.replace(/^https?:\/\//i, '');

const stripUrlParamsAndHash = (value: string) => value.split(/[?#]/)[0];

const extractSocialHandle = (value: string, markers: string[]) => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return '';
  }

  let normalizedValue = stripProtocol(trimmedValue);
  const normalizedValueLower = normalizedValue.toLowerCase();

  for (const marker of markers) {
    const normalizedMarker = stripProtocol(marker).toLowerCase();
    const markerIndex = normalizedValueLower.indexOf(normalizedMarker);

    if (markerIndex >= 0) {
      normalizedValue = normalizedValue.slice(markerIndex + normalizedMarker.length);
      break;
    }
  }

  return stripUrlParamsAndHash(stripProtocol(normalizedValue))
    .replace(/^\/+/, '')
    .trim();
};

export const normalizeSocialHandle = (
  value: string,
  platform: 'youtube' | 'instagram' | 'facebook' | 'linkedin' | 'x' | 'tiktok'
) => {
  const markersByPlatform: Record<typeof platform, string[]> = {
    youtube: ['www.youtube.com/@', 'youtube.com/@', 'www.youtube.com/channel/', 'youtube.com/channel/'],
    instagram: ['www.instagram.com/', 'instagram.com/'],
    facebook: ['www.facebook.com/', 'facebook.com/'],
    linkedin: [
      'www.linkedin.com/in/',
      'linkedin.com/in/',
      'www.linkedin.com/ln/',
      'linkedin.com/ln/',
    ],
    x: ['x.com/', 'www.x.com/', 'twitter.com/', 'www.twitter.com/'],
    tiktok: ['www.tiktok.com/@', 'tiktok.com/@', 'www.tiktok.com/', 'tiktok.com/'],
  };

  const handle = extractSocialHandle(value, markersByPlatform[platform]);

  return platform === 'tiktok' ? handle.replace(/^@+/, '') : handle;
};

export const getProfile = async (
  token: string
): Promise<ProfileResponseModel> => {
  if (token) {
    try {
      const response = await api.get<ProfileResponseModel>('/v1/user', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return normalizeProfileFollowData(normalizeWebsiteUrl(response.data));
    } catch (error) {
      logApiError('Get profile request', error);
      return Promise.reject(error);
    }
  }
  return Promise.reject(new Error('No token provided'));
};

export const getProfessionalProfile = async (
  professionalId: string
): Promise<ProfileResponseModel> => {
  try {
    const response = await api.get<ProfileResponseModel>(
      `/v1/user/professional?id=${professionalId}`
    );
    return normalizeProfileFollowData(normalizeWebsiteUrl(response.data));
  } catch (error) {
    logApiError('Get professional profile request', error);
    return Promise.reject(error);
  }
};

export const getProfessionalProfileByUsername = async (
  username: string
): Promise<UserDocument> => {
  try {
    const response = await api.get<UserDocument>(
      `/v1/user/professional-username?username=${username}`
    );
    return normalizeWebsiteUrl(response.data);
  } catch (error) {
    logApiError('Get professional profile by username request', error);
    return Promise.reject(error);
  }
};

export const editProfile = async (
  body: EditProfilePayload,
  token: string
): Promise<ProfileResponseModel | null> => {
  try {
    const {
      categories,
      searchTerms,
      linkedInUrl,
      displayName,
      ...basePayload
    } = body;

    const normalizedYouTubeHandle = normalizeSocialHandle(body.youTubeUrl, 'youtube');
    const normalizedInstagramHandle = normalizeSocialHandle(
      body.instagramUrl,
      'instagram'
    );
    const normalizedLinkedInHandle = normalizeSocialHandle(linkedInUrl, 'linkedin');
    const normalizedFacebookHandle = normalizeSocialHandle(body.facebookUrl, 'facebook');
    const normalizedTikTokHandle = normalizeSocialHandle(body.tikTokUrl, 'tiktok');
    const normalizedXHandle = normalizeSocialHandle(body.xUrl, 'x');

    const requestPayload = {
      ...basePayload,
      ...(body.isProfessional ? { displayName } : {}),
      websiteUrl: body.websiteUrl.trim() ? body.websiteUrl.trim() : null,
      userLanguages: {
        languages: body.userLanguages ?? [],
      },
      userSearchTerms: {
        searchTerms:
          searchTerms.split(',').map((term) => ({ name: term })) ?? [],
      },
      userCategories: {
        categories: categories ?? [],
      },
      youTubeUrl: normalizedYouTubeHandle
        ? SocialsPrefixes.YOUTUBE + normalizedYouTubeHandle
        : '',
      instagramUrl: normalizedInstagramHandle
        ? SocialsPrefixes.INSTAGRAM + normalizedInstagramHandle
        : '',
      linkedinUrl: normalizedLinkedInHandle
        ? SocialsPrefixes.LINKEDIN + normalizedLinkedInHandle
        : '',
      facebookUrl: normalizedFacebookHandle
        ? SocialsPrefixes.FACEBOOK + normalizedFacebookHandle
        : '',
      tikTokUrl: normalizedTikTokHandle
        ? SocialsPrefixes.TIKTOK + normalizedTikTokHandle
        : '',
      xUrl: normalizedXHandle ? SocialsPrefixes.X + normalizedXHandle : '',
    };

    console.log('Edit profile request payload', requestPayload);

    const response = await api.post<ProfileResponseModel>(
      '/v1/user/edit-profile',
      requestPayload,
      {
        headers: {
          'Content-Type': `application/json`,
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return normalizeProfileFollowData(normalizeWebsiteUrl(response.data));
  } catch (error) {
    logApiError('Edit profile request', error);
    return Promise.reject(error);
  }
};

export const checkUsernameExists = async (
  username: string
): Promise<boolean> => {
  try {
    const response = await api.get<boolean>(
      `/v1.0/user/username-exists?username=${encodeURIComponent(username)}`
    );
    return response.data;
  } catch (error) {
    logApiError('Username availability check request', error);
    return Promise.reject(error);
  }
};

export const getEmail = async (token: string): Promise<string> => {
  try {
    const response = await api.get<{ email: string }>('/v1/user/email', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.email;
  } catch (error) {
    logApiError('Get email request', error);
    return Promise.reject(error);
  }
};

export const sendProfileShareMilestone = async (): Promise<boolean> => {
  try {
    const response = await api.post<{ sent: boolean }>('/v1/user/share-profile');
    return response.data.sent;
  } catch (error) {
    logApiError('Share profile milestone request', error);
    return Promise.reject(error);
  }
};

export enum SocialsPrefixes {
  INSTAGRAM = 'https://instagram.com/',
  LINKEDIN = 'www.linkedin.com/in/',
  FACEBOOK = 'www.facebook.com/',
  YOUTUBE = 'https://www.youtube.com/',
  X = 'https://x.com/',
  TIKTOK = 'https://www.tiktok.com/@',
}

export const ProfileValidation = {
  YOUTUBE: yup
    .string()
    .matches(
      /^@[\w-]+$/,
      'YouTube Handle URL must be provided, handle must start with "@" and contain only alphanumeric characters, underscores, or hyphens'
    )
    .nullable(),
  INSTAGRAM: yup
    .string()
    .matches(
      /^(?!.*\.\.)(?!\.)(?!.*\.$)[a-zA-Z0-9_.]{1,30}$/,
      'Instagram handle must be 1–30 characters, contain only letters, numbers, underscores, or periods, and cannot start, end, or have consecutive periods'
    ),
  LINKEDIN: yup
    .string()
    .matches(
      /^[a-zA-Z0-9-]+$/,
      'LinkedIn handle must only contain alphanumeric characters and hyphens'
    ),
  FACEBOOK: yup
    .string()
    .matches(
      /^(?=.{5,})(?!.*\.\.)(?!.*\.$)[a-zA-Z0-9.]+$/,
      'Facebook handle must be at least 5 characters long, contain only alphanumeric characters and full stops, and cannot end with a full stop'
    ),
  X: yup
    .string()
    .matches(
      /^[a-zA-Z0-9_]{4,15}$/,
      'X (Twitter) handle must be between 4 and 15 characters long and can only contain letters, numbers, and underscores'
    ),
  TIKTOK: yup
    .string()
    .matches(
      /^[a-zA-Z0-9_.]{2,24}$/,
      'TikTok handle must be between 2 and 24 characters long and can only contain letters, numbers, underscores, and periods'
    ),
};
